import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/useI18n';
import { useAuth } from '../contexts/AuthContext';
import { projectApi } from '../api/client';
import { getAvatarById } from '../utils/constants';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
const LINE_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316'];

export default function Stats() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const pid = searchParams.get('pid');
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (pid) {
      loadStats();
    }
  }, [pid, period]);

  async function loadStats() {
    setLoading(true);
    try {
      const res = await projectApi.getStats(pid, period);
      if (res.success) {
        setStats(res.data);
      } else {
        alert(res.message || t('loadingFailed'));
      }
    } catch (err) {
      console.error(t('loading') + ' ' + t('error'), err);
    } finally {
      setLoading(false);
    }
  }

  // 饼图数据
  const pieDataByCount = stats?.by_count?.map((item, index) => ({
    name: item.reason,
    value: item.count,
    color: COLORS[index % COLORS.length],
  })) || [];

  const pieDataByPositive = stats?.by_positive?.map((item, index) => ({
    name: item.reason,
    value: parseFloat(item.total_points),
    color: COLORS[index % COLORS.length],
  })) || [];

  const pieDataByNegative = stats?.by_negative?.map((item, index) => ({
    name: item.reason,
    value: Math.abs(parseFloat(item.total_points)),
    color: COLORS[index % COLORS.length],
  })) || [];



  // 曲线图数据处理部分
  const topReasons = stats?.top_reasons || [];
  const timeSeries = stats?.time_series || [];
  console.log("stats",stats);

  const lineData = timeSeries.map(item => {
    console.log('item',item);
    // 1. 确保日期存在
    const entry = {
      date: item.date ? item.date.slice(5) : '',
      total_count: Number(item.total_count || 0), 
    };

    // 3. 处理各个分类的数值
    for (let i = 1; i <= 5; i++) {
      const key = 'reason_' + i;
      // 修复：不要使用 if(item[key])，改用 undefined 检查或直接赋值并转换
      entry[key] = item[key] !== undefined ? Number(item[key]) : 0;
    }
    return entry;
  });  

  // 渲染饼图
  const renderPieChart = (data) => {
    if (data.length === 0) {
      return <div className="text-center py-8 text-gray-400 text-xs">{t('noData')}</div>;
    }

    return (
      <div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={60}
                paddingAngle={3}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value, name]}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-1">
          {data.slice(0, 6).map((item, index) => (
            <div key={index} className="flex items-center gap-1 text-[10px]">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }}></div>
              <span className="truncate text-gray-600">{item.name}</span>
              <span className="text-gray-400 ml-auto">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-indigo-600 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex items-center">
          <button
            onClick={() => navigate(`/?pid=${pid}`)}
            className="mr-3 p-1 hover:bg-indigo-500 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
          </button>
          <h1 className="text-lg font-bold">{t('projectStats')}</h1>
        </div>
      </nav>

      <main className="container mx-auto p-4 max-w-xl space-y-6">
        {/* 时间周期选择 */}
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          <div className="flex justify-between items-center gap-2">
            {['week', 'month', 'year', 'all'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                  period === p ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {t(`period_${p}`)}
              </button>
            ))}
          </div>
        </div>

        {/* 统计摘要 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-4">{t('summary')}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-indigo-50 rounded-xl">
              <p className="text-xl font-black text-indigo-600">{stats?.summary?.total_count || 0}</p>
              <p className="text-[9px] text-indigo-400 mt-1">{t('totalRecords')}</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <p className="text-xl font-black text-green-600">+{stats?.summary?.total_positive || 0}</p>
              <p className="text-[9px] text-green-400 mt-1">{t('totalPositive')}</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-xl">
              <p className="text-xl font-black text-red-600">{stats?.summary?.total_negative || 0}</p>
              <p className="text-[9px] text-red-400 mt-1">{t('totalNegative')}</p>
            </div>
          </div>
        </div>

        {/* 饼图1：按次数统计 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-4">{t('reasonByCount')}</h3>
          {renderPieChart(pieDataByCount)}
        </div>

        {/* 饼图2：按正分统计 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-green-500 uppercase tracking-widest mb-4">{t('positiveDistribution')}</h3>
          {renderPieChart(pieDataByPositive)}
        </div>

        {/* 饼图3：按负分统计 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-red-500 uppercase tracking-widest mb-4">{t('negativeDistribution')}</h3>
          {renderPieChart(pieDataByNegative)}
        </div>

        {/* 曲线图 - 时间趋势 */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-4">{t('trendChart')}</h3>
          {lineData.length > 0 ? (
            <>
              <div className="w-full h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} />
                    {topReasons.slice(0, 5).map((reason, idx) => (
                      <Line
                        key={reason}
                        type="monotone"
                        dataKey={'reason_' + (idx + 1)}
                        stroke={LINE_COLORS[idx]}
                        strokeWidth={2}
                        dot={{ fill: LINE_COLORS[idx], strokeWidth: 1, r: 3 }}
                        name={reason}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="total_count"
                      stroke="#22c55e"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#22c55e', strokeWidth: 1, r: 3 }}
                      name={t('dailyCount')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 justify-center">
                {topReasons.slice(0, 5).map((reason, idx) => (
                  <div key={reason} className="flex items-center gap-1 text-[10px]">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: LINE_COLORS[idx] }}></div>
                    <span className="text-gray-600 truncate max-w-[60px]">{reason}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1 text-[10px]">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }}></div>
                  <span className="text-gray-600">{t('dailyCount')}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-400 text-xs">{t('noData')}</div>
          )}
        </div>
      </main>
    </div>
  );
}